import React, { useState, useEffect, useRef, useMemo } from 'react';
import { normalizarTexto } from '../../utils/texto';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Check, Search, User, Users, Layers } from 'lucide-react';
import { AsignadoAvatar } from './utils/displays';
import { SHADOW, FOCUS } from './utils/uiTokens';

// Selector de "de quién son las tareas" para la vista Mis tareas.
// valor: 'me' | 'equipo' | 'todos' | <empleado_id como string>
// personas: [{id, nombre, activas, vencidas, es_equipo, soy_yo}]
// Siempre acotado a proyectos donde el usuario es miembro (lo garantiza el backend).

const OPCIONES_FIJAS = [
  { value: 'me', label: 'Mis tareas', icon: User },
  { value: 'equipo', label: 'Mi equipo', icon: Users, soloConEquipo: true },
  { value: 'todos', label: 'Todas las personas', icon: Layers },
];

export const labelDeVista = (valor, personas = []) => {
  const fija = OPCIONES_FIJAS.find((o) => o.value === valor);
  if (fija) return fija.label;
  const p = personas.find((x) => String(x.id) === String(valor));
  return p ? p.nombre : 'Persona';
};

const Carga = ({ activas, vencidas }) => (
  <span className="ml-auto flex items-center gap-2 text-[11px] tabular-nums flex-shrink-0">
    <span className="text-slate-400">{activas}</span>
    {vencidas > 0 && <span className="text-rose-600 font-medium">{vencidas} venc.</span>}
  </span>
);

const PersonaSelector = ({ valor, personas = [], tengoEquipo = false, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => { if (!open) setQ(''); }, [open]);

  const equipo = useMemo(() => personas.filter((p) => p.es_equipo), [personas]);
  const filtradas = useMemo(() => {
    const t = normalizarTexto(q).trim();
    const base = personas.filter((p) => !p.soy_yo);
    return t ? base.filter((p) => normalizarTexto(p.nombre).includes(t)) : base;
  }, [personas, q]);

  const elegir = (v) => { onChange(String(v)); setOpen(false); };

  const personaActual = personas.find((p) => String(p.id) === String(valor));
  const fija = OPCIONES_FIJAS.find((o) => o.value === valor);
  const IconoActual = fija?.icon || User;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex items-center gap-2 h-8 pl-2 pr-2.5 rounded-md border text-sm transition-colors ${FOCUS.button} ${
          valor === 'me'
            ? 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
            : 'bg-cyan-50 border-cyan-200 text-cyan-800 hover:border-cyan-300'
        }`}
      >
        {personaActual ? (
          <AsignadoAvatar nombre={personaActual.nombre} size={20} />
        ) : (
          <IconoActual size={14} strokeWidth={2} className={valor === 'me' ? 'text-slate-400' : 'text-cyan-600'} />
        )}
        <span className="font-medium truncate max-w-[180px]">{labelDeVista(valor, personas)}</span>
        <ChevronDown size={13} strokeWidth={2} className="opacity-60" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className={`absolute right-0 top-full mt-1.5 w-[300px] bg-white rounded-lg z-30 overflow-hidden ${SHADOW.dropdown}`}
            role="listbox"
          >
            <div className="p-1">
              {OPCIONES_FIJAS.filter((o) => !o.soloConEquipo || tengoEquipo).map((o) => {
                const Icon = o.icon;
                const activo = valor === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={activo}
                    onClick={() => elegir(o.value)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm text-left transition-colors ${
                      activo ? 'bg-cyan-50 text-cyan-800' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={14} strokeWidth={2} className={activo ? 'text-cyan-600' : 'text-slate-400'} />
                    <span className="flex-1">{o.label}</span>
                    {o.value === 'equipo' && (
                      <span className="text-[11px] text-slate-400 tabular-nums">{equipo.length}</span>
                    )}
                    {activo && <Check size={13} strokeWidth={2.5} className="text-cyan-600" />}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-slate-100">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                <Search size={13} className="text-slate-400" strokeWidth={2} />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar persona"
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto p-1">
                {filtradas.length === 0 ? (
                  <p className="px-2.5 py-3 text-xs text-slate-500">
                    {q ? 'Nadie con ese nombre tiene tareas abiertas en tus proyectos.' : 'Nadie más tiene tareas abiertas en tus proyectos.'}
                  </p>
                ) : (
                  filtradas.map((p) => {
                    const activo = String(valor) === String(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        role="option"
                        aria-selected={activo}
                        onClick={() => elegir(p.id)}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm text-left transition-colors ${
                          activo ? 'bg-cyan-50 text-cyan-800' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <AsignadoAvatar nombre={p.nombre} size={20} />
                        <span className="truncate">{p.nombre}</span>
                        {p.es_equipo && (
                          <span className="text-[10px] px-1.5 py-px rounded bg-slate-100 text-slate-500 flex-shrink-0">Equipo</span>
                        )}
                        <Carga activas={p.activas} vencidas={p.vencidas} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonaSelector;
